import { ChevronRight } from '@mui/icons-material';
import {
  Button as MUIButton,
  buttonClasses,
  type ButtonTypeMap,
  type ButtonProps as MUIButtonProps
} from '@mui/material';
import { merge } from 'lodash';
import type { ButtonHTMLAttributes } from 'react';
import { FontNormalSxProps } from '../../../_constants';

interface CommonProps {
  /**
   * Data-qa tag for E2E test purposes
   */
  'data-qa'?: string;
  /**
   * Defines the colour and style of the button
   */
  type: 'primary' | 'secondary' | 'text';
}

export type AoaButtonProps = Omit<MUIButtonProps<ButtonTypeMap['defaultComponent'], CommonProps>, 'variant'> & {
  readonly buttonType?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

/**
 * Constructs a button using pre-defined Rijks styling
 *
 * @param props - Props to pass to the button
 * @example
 * ```jsx
 * <AoaButton onClick={() => console.log()} type='primary'>button</AoaButton>
 * ```
 */

export function AoaButton({ type, startIcon, 'data-qa': dataQa, children, buttonType, ...props }: AoaButtonProps) {
  let variant: 'contained' | 'outlined' | 'text' = 'contained';

  if (type === 'text') {
    variant = 'text';
  }

  if (type === 'secondary') {
    variant = 'outlined';
  }

  return (
    <MUIButton
      {...props}
      data-qa={dataQa}
      disableElevation
      endIcon={type === 'text' && <ChevronRight />}
      startIcon={type !== 'text' && startIcon}
      sx={merge(
        {
          // Styles that apply to all button types and colors
          appearance: 'none',
          border: '1px solid',
          borderRadius: 0,
          boxSizing: 'border-box',
          color: 'var(--color-text-light)',
          cursor: 'pointer',
          display: 'inline-flex',
          lineHeight: 'var(--font-lineheight-text)',
          minHeight: 20,
          padding: '12px 16px',
          position: 'relative',
          textDecoration: 'none',
          textShadow: 'none',
          textTransform: 'none',
          verticalAlign: 'middle',

          ':focus': {
            outline: '2px dashed var(--color-text)',
            outlineOffset: '3px',
            boxShadow: 'none'
          },

          // Styles that apply to contained (type === primary)
          [`&.${buttonClasses.contained}`]: {
            [`&.${buttonClasses.colorPrimary}`]: {
              backgroundColor: 'var(--color-primary)',
              borderColor: 'var(--color-primary)',

              ':hover, :active, :focus': {
                backgroundColor: 'var(--color-primary-hover)'
              }
            },

            [`&.${buttonClasses.colorError}`]: {
              backgroundColor: 'var(--color-error)',
              borderColor: 'var(--color-error)',

              ':hover, :active, :focus': {
                backgroundColor: 'var(--color-error-hover)'
              }
            },

            [`&.${buttonClasses.disabled}`]: {
              backgroundColor: 'var(--color-disabled)',
              borderColor: 'var(--color-disabled)',
              color: 'var(--color-text-disabled)'
            },

            ':hover, :active, :focus': {
              color: 'var(--color-text-light)'
            }
          },

          // Styles that apply to outlined (type === secondary)
          [`&.${buttonClasses.outlined}`]: {
            background: 'none',
            border: '1px solid var(--color-primary)',
            boxSizing: 'border-box',

            [`&.${buttonClasses.colorPrimary}`]: {
              color: 'var(--color-primary)',
              borderColor: 'var(--color-primary)',

              ':hover, :active, :focus': {
                borderColor: 'var(--color-primary-hover)',
                color: 'var(--color-primary-hover)'
              }
            },

            [`&.${buttonClasses.colorError}`]: {
              color: 'var(--color-error)',
              borderColor: 'var(--color-error)',

              ':hover, :active, :focus': {
                borderColor: 'var(--color-error-hover)',
                color: 'var(--color-error-hover)'
              }
            },

            ':disabled': {
              backgroundColor: 'transparent',
              borderColor: 'var(--color-text-disabled)',
              color: 'var(--color-text-disabled)'
            }
          },

          // Styles that apply to text (type === text)
          [`&.${buttonClasses.text}`]: {
            background: 'none',
            borderColor: 'transparent',
            display: 'inline-block',
            textTransform: 'uppercase',
            fontWeight: 'bold',
            fontSize: '1.075rem',
            verticalAlign: 'middle',

            [`&.${buttonClasses.colorPrimary}`]: {
              color: 'var(--color-primary)',

              ':hover': {
                backgroundColor: 'var(--color-rijks-skyblue-light)'
              }
            },

            [`&.${buttonClasses.colorError}`]: {
              color: 'var(--color-error)',

              ':hover': {
                backgroundColor: 'var(--color-rijks-red-1-light)'
              }
            },

            [`&.${buttonClasses.endIcon}`]: {
              width: '1rem',
              height: '1rem',
              verticalAlign: 'text-top'
            },

            ':disabled': {
              backgroundColor: 'transparent',
              color: 'var(--color-text-disabled)',

              [`&.${buttonClasses.endIcon}`]: {
                fill: 'var(--color-text-disabled)'
              }
            }
          }
        },
        props.sx,
        FontNormalSxProps
      )}
      type={buttonType}
      variant={variant}
    >
      {children}
    </MUIButton>
  );
}
